import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  OnDestroy,
  OnInit
} from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import DropDownOptions from '@approot/modals/trade-capture/schedule-reports/report-dropdown-options.json';
import {
  LifecycleBlotterGridStateKey as BlotterGridStateKey,
  LifecycleBlotterTimeZoneStateKey as BlotterTimeZoneStateKey
} from '@approot/models';
import { AgGridService, UserStorageService } from '@approot/services';
import { clone, SubscriptionSink } from '@approot/utilities';
import {
  AgGridEvent,
  ColDef,
  GridReadyEvent,
  SideBarDef
} from 'ag-grid-community';
import dayjs from 'dayjs';
import { isEqual } from 'lodash';
import {
  auditTime,
  debounceTime,
  distinctUntilChanged,
  take
} from 'rxjs/operators';
import { sorter } from '../shared/utils';
import { applyColumnState } from './lifecycle-blotter.funcs';
import { LifecycleBlotterService as BlotterService } from './lifecycle-blotter.service';
import {
  LifecycleBlotterState as BlotterState,
  RunningState
} from './lifecycle-blotter.types';

const NAME = 'Lifecycle';
const TEMPLATE = `<span style="padding: 10px; border: 2px solid #444;">No ${NAME} loaded</span>`;

@Component({
  selector: 'app-lifecycle-blotter',
  templateUrl: './lifecycle-blotter.component.html',
  styleUrls: ['./lifecycle-blotter.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LifecycleBlotterComponent implements OnInit, OnDestroy {
  public sideBarDef: SideBarDef;
  public state: BlotterState = {};
  public entities: string[] = [];
  public runningState: RunningState = RunningState.Idle;
  public RunningState = RunningState;
  public readonly defaultColDef: ColDef = {
    sortable: true,
    enableRowGroup: true,
    resizable: true
  };
  public headers: any[] = [];
  public rowData: any[] = [];
  public readonly overlayNoRowsTemplate = TEMPLATE;
  public form: FormGroup = new FormGroup({
    entity: new FormControl<string>(null),
    timezone: new FormControl<string>('Europe/London'),
    start_time: new FormControl<string>('00:00'),
    start_date: new FormControl<string>(dayjs().format('YYYY-MM-DD')),
    time: new FormControl<string>('23:59'),
    date: new FormControl<string>(dayjs().add(6, 'day').format('YYYY-MM-DD'))
  });
  public timezones: string[] = DropDownOptions.timezones;

  private subsink = new SubscriptionSink();
  private agGridService = inject(AgGridService);
  private changeRef = inject(ChangeDetectorRef);
  private blotterService = inject(BlotterService);
  private userStorageService = inject(UserStorageService);

  public constructor() {
    this.sideBarDef = this.agGridService.sideBarDef;
    this.subsink.sink = this.form.valueChanges
      .pipe(
        debounceTime(250),
        distinctUntilChanged((a, b) => isEqual(a, b))
      )
      .subscribe(v => this.blotterService.update({ selectedEntry: v }));

    this.subsink.sink = this.form
      .get('timezone')
      .valueChanges.pipe(distinctUntilChanged((a, b) => isEqual(a, b)))
      .subscribe(tz =>
        this.userStorageService.setItem(BlotterTimeZoneStateKey, tz)
      );

    const tz =
      this.userStorageService.getItem(BlotterTimeZoneStateKey) ||
      'Europe/London';
    this.form.get('timezone').setValue(tz, { emitEvent: false });
  }

  public ngOnInit(): void {
    // initial state.  Although we resubscribe to stateChanged in onGridReady, we
    // get it immediately (i.e., no `auditTime()`) so that the form is populated with
    // the correct entity, otherwise there's an annoying delay.
    this.subsink.sink = this.blotterService.stateChanged
      .pipe(take(1))
      .subscribe({
        next: state => this.updateOnStateChange(state)
      });
  }

  public ngOnDestroy(): void {
    this.subsink.unsubscribe();
  }

  public onGridReady(params: GridReadyEvent) {
    this.subsink.sink = this.blotterService.stateChanged
      .pipe(
        auditTime(250) // Don't update too often
      )
      .subscribe({
        next: state => this.updateOnStateChange(state)
      });

    const columnState = this.userStorageService.getItem(BlotterGridStateKey);
    // sometimes we get a string of 'undefined' from local storage so ignore that
    if (columnState && columnState !== 'undefined') {
      applyColumnState(params.columnApi, columnState);
    }
  }

  public canRunReport(): boolean {
    const form = this.form.getRawValue();
    let validDate = /^\d\d\d\d-\d\d-\d\d$/.test(form['date']);
    let validTime = /^\d\d:\d\d$/.test(form['time']);
    if (form['date'] && !validDate) {
      return false;
    }
    if (form['time'] && !validTime) {
      return false;
    }
    if (validTime && !validDate) {
      return false;
    }
    validDate = /^\d\d\d\d-\d\d-\d\d$/.test(form['start_date']);
    validTime = /^\d\d:\d\d$/.test(form['start_time']);
    if (form['start_date'] && !validDate) {
      return false;
    }
    if (form['start_time'] && !validTime) {
      return false;
    }
    if (validTime && !validDate) {
      return false;
    }
    if (form['entity'] == null) {
      return false;
    }
    return true;
  }

  public runReport() {
    const form = this.form.getRawValue();
    if (!this.canRunReport()) {
      return;
    }

    this.blotterService.runReport(form);
  }

  public cancelReport() {
    this.blotterService.cancelReport();
  }

  public gridStateChanged(event: AgGridEvent): void {
    const allState = event.columnApi.getColumnState();
    if (allState) {
      this.userStorageService.setItem(BlotterGridStateKey, allState);
    } else {
      this.userStorageService.removeItem(BlotterGridStateKey);
    }
  }

  private updateOnStateChange(state: BlotterState) {
    this.state = state;
    // We don't want to update the grid if the data unchanged
    if (this.rowData.length !== state.rows.length) {
      this.rowData = state.rows;
    }
    if (this.entities.length !== state.entities.length) {
      this.entities = clone(state).entities.sort(sorter);
    }
    if (this.headers.length !== state.headers.length) {
      this.headers = state.headers;
    }
    this.form
      .get('entity')
      .setValue(state.selectedEntry?.entity, { emitEvent: false });
    this.changeRef.markForCheck();
  }
}
